import {
  FileArrowUpIcon,
  InfoIcon,
  PlusCircleIcon,
} from '@phosphor-icons/react';
import { useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { FileMimeTypes, GenericTable, Spinner } from '@/modules/common';
import { EventParticipant } from '@/modules/eventsV2/types';
import { useEventFilters } from '@/modules/eventsV2/hooks';

import {
  CreateParticipantTypeDialog,
  ParticipantDetailsDialog,
  ParticipantsEventActions,
  ParticipantsTableFilters,
  SendSmsDialog,
} from './components';
import { useParticipantsTab } from './hooks/use-participants-tab';

export function ParticipantsTab() {
  const fileInputRef = useRef(null);

  const [selectedRow, setSelectedRow] = useState<EventParticipant | null>(null);
  const [isSendSmsDialogOpen, setIsSendSmsDialogOpen] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
    []
  );
  const [
    isCreateParticipantTypeDialogOpen,
    setIsCreateParticipantTypeDialogOpen,
  ] = useState(false);

  const { filters, filtersChange } = useEventFilters();

  const {
    eventId,
    columns,
    participants,
    participantTypesOptions,
    participantTypes,
    isLoading,
    formattedParticipants,
    isGeneratingReport,
    getGenerateReportUrl,
    pickFileAndUpload,
  } = useParticipantsTab(filters);

  const selectedParticipantsIds = useMemo(() => {
    return participants
      .filter((participant) => selectedParticipants.includes(participant.id))
      .map((participant) => participant.id);
  }, [selectedParticipants, filters]);

  const pickFile = () => {
    fileInputRef.current.click();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="font-semibold text-lg">Participants Table</p>

      <div className="flex flex-col gap-4">
        <div className="py-5 flex gap-5 items-center justify-between bg-zinc-900 rounded-lg border border-zinc-800 p-4">
          <div className="flex gap-2 items-center">
            <InfoIcon />
            <div className="text-xs">
              <p className="font-semibold">Manage existing participant types</p>
              <p>Use the "Participant Types button" to view or update them.</p>
            </div>
          </div>

          <Button onClick={() => setIsCreateParticipantTypeDialogOpen(true)}>
            Participant Types
          </Button>
        </div>

        <GenericTable<EventParticipant>
          pinEnabled
          columns={columns}
          name="participants"
          data={participants}
          emptyCellClassName="h-20"
          sizeStyleProperty="minWidth"
          tableCellClassName="px-2 py-1"
          tableRowClassName="hover:bg-transparent"
          initialState={{
            columnPinning: {
              right: ['actions'],
              left: ['select', 'firstName', 'lastName', 'externalId'],
            },
          }}
          onClick={setSelectedRow}
          isFiltered={Object.keys(filters).length > 0}
          emptyTitle="Create Participant Types before uploading the Participant CSV file."
          emptyDescription="You can create “Participant Types” only in published event."
          emptyButton={
            <Button onClick={() => setIsCreateParticipantTypeDialogOpen(true)}>
              <PlusCircleIcon />
              Create Participant Types
            </Button>
          }
          onRowSelectionChange={setSelectedParticipants}
          disabledColumnsIdsToClick={[
            'select',
            'type',
            'usagesLeft',
            'actions',
          ]}
        >
          <div className="flex gap-4 items-center justify-center">
            <ParticipantsTableFilters
              types={participantTypesOptions}
              filters={filters}
              onFilters={filtersChange}
            />

            <div className="flex gap-2">
              <Button
                disabled={!participantTypes.length}
                variant="outline"
                onClick={pickFile}
                data-testid="upload-file-button"
              >
                <FileArrowUpIcon />
                Upload CSV
                <input
                  id="csv-file"
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  data-testid="upload-file-input"
                  onChange={pickFileAndUpload}
                  accept={FileMimeTypes.Csv}
                />
              </Button>

              {formattedParticipants.length > 0 && (
                <ParticipantsEventActions
                  eventId={eventId}
                  onGenerateReport={getGenerateReportUrl}
                  isGeneratingReport={isGeneratingReport}
                />
              )}
            </div>

            {Object.keys(selectedParticipants).length > 0 && (
              <Button onClick={() => setIsSendSmsDialogOpen(true)}>
                Send SMS
              </Button>
            )}
          </div>
        </GenericTable>
      </div>

      <CreateParticipantTypeDialog
        isOpen={isCreateParticipantTypeDialogOpen}
        eventId={eventId}
        participants={formattedParticipants}
        participantTypes={participantTypes}
        onOpenChange={setIsCreateParticipantTypeDialogOpen}
      />

      <SendSmsDialog
        eventId={eventId}
        isOpen={isSendSmsDialogOpen}
        onOpenChange={setIsSendSmsDialogOpen}
        participantIds={selectedParticipantsIds}
      />

      {selectedRow && (
        <ParticipantDetailsDialog
          isOpen={!!selectedRow}
          eventId={eventId}
          selectedParticipant={selectedRow}
          participantTypes={participantTypes}
          onOpenChange={() => setSelectedRow(null)}
        />
      )}
    </div>
  );
}
